/* =========================================================================
   ClueVine — sound
   A tiny WebAudio synthesizer. Every sound is generated from oscillators at
   runtime — no audio assets, nothing copyrighted. A module-level singleton lets
   event handlers play sounds imperatively; useSound() binds it to the user's
   sound setting and exposes resume() for the autoplay-policy unlock.
   ========================================================================= */

import { useEffect, useMemo } from 'react';
import { useSettings } from '../state/SettingsContext';

export type SoundName =
  | 'key'
  | 'select'
  | 'delete'
  | 'submit'
  | 'invalid'
  | 'reveal'
  | 'hint'
  | 'tick'
  | 'win'
  | 'lose';

interface Tone {
  freq: number;
  dur: number;
  type: OscillatorType;
  gain?: number;
}

class SoundManager {
  private ctx: AudioContext | null = null;
  private enabled = true;

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  private context(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    try {
      if (!this.ctx) {
        const AC =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AC) return null;
        this.ctx = new AC();
      }
      return this.ctx;
    } catch {
      return null;
    }
  }

  /** Unlock audio on the first user gesture (autoplay policy). */
  resume(): void {
    const ctx = this.context();
    if (ctx && ctx.state === 'suspended') void ctx.resume();
  }

  private blip(ctx: AudioContext, tone: Tone, when = 0): void {
    const { freq, dur, type, gain = 0.06 } = tone;
    const t0 = ctx.currentTime + when;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  }

  play(name: SoundName): void {
    if (!this.enabled) return;
    const ctx = this.context();
    if (!ctx) return;
    if (ctx.state === 'suspended') void ctx.resume();

    switch (name) {
      case 'key':
        this.blip(ctx, { freq: 330, dur: 0.05, type: 'sine', gain: 0.045 });
        break;
      case 'select':
        this.blip(ctx, { freq: 440, dur: 0.06, type: 'triangle', gain: 0.05 });
        break;
      case 'delete':
        this.blip(ctx, { freq: 220, dur: 0.05, type: 'sine', gain: 0.045 });
        break;
      case 'submit':
        this.blip(ctx, { freq: 392, dur: 0.08, type: 'triangle' });
        break;
      case 'invalid':
        this.blip(ctx, { freq: 150, dur: 0.18, type: 'sawtooth', gain: 0.045 });
        break;
      case 'reveal':
        this.blip(ctx, { freq: 523, dur: 0.07, type: 'sine' });
        break;
      case 'hint':
        this.blip(ctx, { freq: 587, dur: 0.1, type: 'triangle' });
        break;
      case 'tick':
        this.blip(ctx, { freq: 880, dur: 0.03, type: 'sine', gain: 0.03 });
        break;
      case 'win':
        [523, 659, 784, 1047].forEach((f, i) =>
          this.blip(ctx, { freq: f, dur: 0.16, type: 'triangle', gain: 0.06 }, i * 0.09),
        );
        break;
      case 'lose':
        [330, 247].forEach((f, i) =>
          this.blip(ctx, { freq: f, dur: 0.22, type: 'sine', gain: 0.06 }, i * 0.12),
        );
        break;
    }
  }
}

export const sounds = new SoundManager();

export interface SoundPlayer {
  play: (name: SoundName) => void;
  resume: () => void;
}

export function useSound(): SoundPlayer {
  const { settings } = useSettings();
  useEffect(() => {
    sounds.setEnabled(settings.sound);
  }, [settings.sound]);
  return useMemo(() => ({ play: (n: SoundName) => sounds.play(n), resume: () => sounds.resume() }), []);
}
